import React, { useState } from 'react';
import { formatMexicoDateTime as formatDate } from '../../dateUtils';
import { AlertTriangle, Clock, AlertCircle, ExternalLink, Filter } from 'lucide-react';

const SLAAlertsTable = ({ alerts = [], cardClasses = '' }) => {
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterPrioridad, setFilterPrioridad] = useState('all');
  const isLightSurface = cardClasses.includes('bg-white/82') || cardClasses.includes('bg-white/80');
  const selectClasses = isLightSurface
    ? 'bg-white border border-violet-200 text-slate-900 focus:ring-violet-500'
    : 'bg-[#1b1038] border border-violet-300/30 text-violet-50 focus:ring-violet-400';
  const optionClasses = isLightSurface ? 'bg-white text-slate-900' : 'bg-[#1b1038] text-violet-50';

  const getAlertIcon = (tipo) => {
    switch (tipo) {
      case 'sla_primera_respuesta':
        return <AlertTriangle className="w-5 h-5 text-red-400" />;
      case 'sla_resolucion':
        return <AlertCircle className="w-5 h-5 text-orange-400" />;
      case 'vencimiento_ticket':
        return <Clock className="w-5 h-5 text-yellow-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getAlertLabel = (tipo) => {
    switch (tipo) {
      case 'sla_primera_respuesta':
        return 'Primera Respuesta';
      case 'sla_resolucion':
        return 'Resolución';
      case 'vencimiento_ticket':
        return 'Vencimiento';
      default:
        return tipo;
    }
  };

  const getPriorityColor = (prioridad) => {
    switch (prioridad) {
      case 'Alta':
        return 'bg-red-500/20 text-red-400';
      case 'Media':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'Baja':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getUrgencyColor = (tiempoRestante) => {
    if (tiempoRestante <= 1) return 'text-red-400 font-bold animate-pulse';
    if (tiempoRestante <= 4) return 'text-orange-400 font-semibold';
    if (tiempoRestante <= 8) return 'text-yellow-400';
    return 'text-green-400';
  };

  // Filtrar alertas
  const filteredAlerts = alerts.filter(alert => {
    const matchTipo = filterTipo === 'all' || alert.tipo_alerta === filterTipo;
    const matchPrioridad = filterPrioridad === 'all' || alert.prioridad === filterPrioridad;
    return matchTipo && matchPrioridad;
  });

  // Ordenar por tiempo restante (más urgente primero)
  const sortedAlerts = [...filteredAlerts].sort((a, b) => 
    a.tiempo_restante_horas - b.tiempo_restante_horas
  );

  return (
    <div className={`${cardClasses} relative z-10 overflow-visible rounded-xl p-6`}>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center space-x-2">
          <AlertTriangle className="w-6 h-6 text-red-400" />
          <span>Tickets con Alertas de SLA ({sortedAlerts.length})</span>
        </h2>

        {/* Filtros */}
        <div className="flex items-center space-x-3">
          <Filter className="w-5 h-5 opacity-50" />
          
          <select
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
            className={`rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${selectClasses}`}
          >
            <option className={optionClasses} value="all">Todos los tipos</option>
            <option className={optionClasses} value="sla_primera_respuesta">Primera Respuesta</option>
            <option className={optionClasses} value="sla_resolucion">Resolución</option>
            <option className={optionClasses} value="vencimiento_ticket">Vencimiento</option>
          </select>

          <select
            value={filterPrioridad}
            onChange={(e) => setFilterPrioridad(e.target.value)}
            className={`rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${selectClasses}`}
          >
            <option className={optionClasses} value="all">Todas las prioridades</option>
            <option className={optionClasses} value="Alta">Alta</option>
            <option className={optionClasses} value="Media">Media</option>
            <option className={optionClasses} value="Baja">Baja</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/20">
              <th className="text-left py-3 px-4 font-semibold">Ticket</th>
              <th className="text-left py-3 px-4 font-semibold">Cliente</th>
              <th className="text-left py-3 px-4 font-semibold">Tipo Alerta</th>
              <th className="text-left py-3 px-4 font-semibold">Prioridad</th>
              <th className="text-left py-3 px-4 font-semibold">Tiempo Restante</th>
              <th className="text-left py-3 px-4 font-semibold">Vence</th>
              <th className="text-left py-3 px-4 font-semibold">Estado</th>
              <th className="text-left py-3 px-4 font-semibold">Acción</th>
            </tr>
          </thead>
          <tbody>
            {sortedAlerts.map((alert, index) => (
              <tr key={index} className="border-b border-white/10 hover:bg-white/5 transition-colors">
                <td className="py-4 px-4">
                  <div className="font-bold text-blue-400">#{alert.ticket_id}</div>
                </td>
                <td className="py-4 px-4">
                  <div className="font-medium">{alert.cuenta_cliente}</div>
                  <div className="text-sm opacity-70">{alert.grupo_soporte}</div>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center space-x-2">
                    {getAlertIcon(alert.tipo_alerta)}
                    <span className="text-sm">{getAlertLabel(alert.tipo_alerta)}</span>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(alert.prioridad)}`}>
                    {alert.prioridad}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <div className={`text-lg font-bold ${getUrgencyColor(alert.tiempo_restante_horas)}`}>
                    {alert.tiempo_restante_horas.toFixed(1)}h
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="text-sm">{formatDate(alert.fecha_vencimiento_sla)}</div>
                </td>
                <td className="py-4 px-4">
                  <span className="text-sm opacity-70">{alert.estado_ticket}</span>
                </td>
                <td className="py-4 px-4">
                  <a
                    href={`https://kionetworks.n.technology/WorkOrder.do?woMode=viewWO&woID=${alert.ticket_id}&PORTALID=1`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <span className="text-sm font-semibold">Ver</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {sortedAlerts.length === 0 && (
          <div className="text-center py-12 opacity-70">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No hay alertas activas con los filtros seleccionados</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SLAAlertsTable;
